import * as yup from 'yup'

/**
 * Validation schema for a single MongoDB collection configuration
 */
export const collectionSchema = yup
  .object()
  .shape({
    name: yup.string().min(1, 'Name too short').required(),
    matchIDs: yup.boolean().default(true),
    initialSync: yup.boolean().default(false),
    dqlStrictMode: yup.boolean().default(false),
    mode: yup.string().default('NATIVE'),
    idMappingFields: yup
      .array()
      .of(
        yup
          .string()
          .min(1, 'ID field cannot be empty')
          .test(
            'no-dollar-prefix',
            'Field cannot start with $',
            (value) => !value?.startsWith('$'),
          )
          .required(),
      )
      .test('unique', 'ID fields must be unique', (value = [], ctx) => {
        const errors = value.reduce((acc, field, idx) => {
          if (value.indexOf(field) !== idx) {
            acc.push(
              ctx.createError({
                path: `idMappingFields.${idx}`,
                message: 'ID fields must be unique',
              }),
            )
          }
          return acc
        }, [] as yup.ValidationError[])
        return errors.length ? new yup.ValidationError(errors) : true
      })
      .required()
      .min(1, 'You must have at least one ID field'),
    dittoTypes: yup
      .object({
        settableCounters: yup
          .array()
          .of(
            yup
              .string()
              .min(1, 'Counter field cannot be empty')
              .test(
                'no-dollar-prefix',
                'Field cannot start with $',
                (value) => !value?.startsWith('$'),
              )
              .required(),
          )
          .test(
            'unique',
            'Counter fields must be unique',
            (value = [], ctx) => {
              const errors = value.reduce((acc, field, idx) => {
                if (value.indexOf(field) !== idx) {
                  acc.push(
                    ctx.createError({
                      path: `${idx}`,
                      message: 'Counter fields must be unique',
                    }),
                  )
                }
                return acc
              }, [] as yup.ValidationError[])
              return errors.length ? new yup.ValidationError(errors) : true
            },
          )
          .default([]),
      })
      .default({ settableCounters: [] }),
  })
  .test('matchCheck', ({ idMappingFields }, ctx) => {
    if (idMappingFields.includes('_id') && idMappingFields.length > 1) {
      return ctx.createError({
        path: `idMappingFields.${idMappingFields.indexOf('_id')}`,
        message: 'You cannot map _id with other fields',
      })
    } else {
      return true
    }
  })
  .test(
    'noCounterIdOverlap',
    'Counter fields cannot overlap with ID fields',
    (value, ctx) => {
      const idFields = new Set(value.idMappingFields || [])
      const counterFields = value.dittoTypes?.settableCounters || []

      for (let i = 0; i < counterFields.length; i++) {
        if (counterFields[i] && idFields.has(counterFields[i])) {
          return ctx.createError({
            path: `dittoTypes.settableCounters.${i}`,
            message: 'Field cannot be both an ID field and a counter field',
          })
        }
      }
      return true
    },
  )

/**
 * Creates a collection schema with uniqueness validation against existing names
 * @param existingNames - Array of existing collection names
 * @param index - Index of the collection being edited (null for new)
 */
export const createCollectionSchema = (
  existingNames: string[],
  index: number | null,
) =>
  collectionSchema.test(
    'uniqueNames',
    'Collection names must be unique',
    (value, ctx) => {
      if (index != null && existingNames.at(index) == value.name) {
        return true
      }

      const names = [...existingNames, value.name]
      const uniqueNames = new Set(names)
      return names.length === uniqueNames.size
        ? true
        : ctx.createError({
            path: 'name',
            message: 'Collection names must be unique',
          })
    },
  )

/**
 * Full validation schema for the MongoDB connector form
 */
export const mongoConnectorSchema = yup
  .object()
  .shape({
    // Username and Password are optional. If left blank, the connection string
    // would not be updated.
    username: yup.string(),
    password: yup.string(),
    connectionString: yup
      .string()
      .min(1, 'Connection string too short')
      .test('is-url', 'Invalid URL', (value) => {
        if (!value) return false
        try {
          new URL(value)
          return true
        } catch {
          return false
        }
      })
      .required(),
    database: yup.string().min(1, 'Database name too short').required(),
    collections: yup
      .array()
      .of(collectionSchema)
      .min(1, 'You must have at least one collection')
      .test(
        'uniqueNames',
        'Collection names must be unique',
        (collections, ctx) => {
          const names = collections?.map((c) => c.name) || []

          const errors = names.reduce((acc, name, idx) => {
            if (names.indexOf(name) !== idx) {
              acc.push(
                ctx.createError({
                  path: `collections.${idx}.name`,
                  message: 'Collection names must be unique',
                }),
              )
            }
            return acc
          }, [] as yup.ValidationError[])

          return errors.length ? new yup.ValidationError(errors) : true
        },
      )
      .required(),
  })
  .test({
    name: 'usernamePasswordBothSetWhenOneSet',
    test: ({ username, password }, ctx) => {
      const isBothSetOrBothNotSet =
        (!username && !password) || (username && password)
      if (isBothSetOrBothNotSet) {
        return true
      }

      return ctx.createError({
        message: 'You must provide both username and password',
        path: !username ? 'username' : 'password',
      })
    },
  })

/**
 * Type inferred from the connector form schema
 */
export type ConnectorFormValues = yup.InferType<typeof mongoConnectorSchema>

/**
 * Type for a single collection form value
 */
export type MongoCollectionFormValue =
  ConnectorFormValues['collections'][number]
